import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProductOffering } from 'anchor-shared/lib/model/entity/productOffering';
import { toTwelveHourTime } from '../../../../../../../../../../core/pure-functions/time/time';
import * as moment from 'moment';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TimeWindow } from 'anchor-shared/lib/model/interface/vesselAvailabilityInterfaces';
import { ConstantMan } from '../../../../../../../../../../core/services/constantMan';

@Component({
  selector: 'app-custom-time-selections',
  templateUrl: './custom-time-selections.component.html',
  styleUrls: ['./custom-time-selections.component.scss'],
})
export class CustomTimeSelectionsComponent implements OnInit {
  @Input() productOffering: BehaviorSubject<Partial<ProductOffering>>;

  times: string[] = ConstantMan.timeSelections;
  selectedStartTime: string;
  customStartTime: string;
  customEndTime: string;
  customStartTime2: any;
  customEndTime2: string;
  customStartTime3: string;
  customEndTime3: string;
  customStartTime4: string;
  customEndTime4: string;
  timeWindows: TimeWindow[];
  nextTime: string;
  customTime1Selected: boolean;
  customTime2Selected: boolean;
  customTime3Selected: boolean;
  customTime4Selected: boolean;
  customTimeWindows: TimeWindow[] = [];
  hoursBetween: number;
  showAdvanced = false;
  flexSelected: boolean;
  isMobile: boolean;

  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.timeWindows = [];
  }

  ngOnInit() {
    this.initTimeWindowVariables();
    this.createAllTimeWindows();
    this.initTimeWindowsWithChecks();
    this.initFlexTimes();
  }

  initTimeWindowVariables() {
    // @ts-ignore
    if (this.productOffering.value.customStartTimeSelection) {
      this.selectedStartTime =
        // @ts-ignore
        this.productOffering.value.customStartTimeSelection;
    } else {
      this.selectedStartTime = '8:00 AM';
    }
    this.hoursBetween = this.productOffering.value.hoursBetween;
  }

  initTimeWindowsWithChecks() {
    if (
      this.productOffering.value &&
      this.productOffering.value.customAvailabilityTimes
    ) {
      this.timeWindows = this.productOffering.value.customAvailabilityTimes;
      this.customTimeWindows.forEach((timeWindow, index) => {
        if (
          this.timeWindows.some(
            (selectedWindow) =>
              toTwelveHourTime(timeWindow.startTime) ===
              selectedWindow.startTime,
          )
        ) {
          switch (index + 1) {
            case 1:
              this.customTime1Selected = true;
              break;
            case 2:
              this.customTime2Selected = true;
              break;
            case 3:
              this.customTime3Selected = true;
              break;
            case 4:
              this.customTime4Selected = true;
              break;
            default:
              console.log('No matching times');
              break;
          }
        }
      });
    }
  }

  createAllTimeWindows() {
    this.timeWindows = [];
    this.customTimeWindows = [];

    this.resetCheckboxes();

    this.customStartTime = this.timeToMilitary(this.selectedStartTime);

    // calculate first time window
    this.customEndTime = this.calculateNextTime(this.customStartTime);

    this.customTimeWindows.push({
      startTime: this.customStartTime,
      endTime: this.customEndTime,
    });
    // calculate second time window
    this.customStartTime2 = this.calculateNextTime(this.customEndTime, true);
    this.customEndTime2 = this.calculateNextTime(this.customStartTime2);
    this.customTimeWindows.push({
      startTime: this.customStartTime2,
      endTime: this.customEndTime2,
    });
    // calculate third time window
    this.customStartTime3 = this.calculateNextTime(this.customEndTime2, true);
    this.customEndTime3 = this.calculateNextTime(this.customStartTime3);
    this.customTimeWindows.push({
      startTime: this.customStartTime3,
      endTime: this.customEndTime3,
    });
    // calculate last time window
    this.customStartTime4 = this.calculateNextTime(this.customEndTime3, true);
    this.customEndTime4 = this.calculateNextTime(this.customStartTime4);
    this.customTimeWindows.push({
      startTime: this.customStartTime4,
      endTime: this.customEndTime4,
    });

    this.productOffering.next({
      ...this.productOffering.value,
      // @ts-ignore
      customStartTimeSelection: this.selectedStartTime,
    });
  }

  calculateNextTime(time: string, isNextWindow?: boolean) {
    let timeInt = parseInt(time);
    let minutes;
    if (time.length === 4) {
      minutes = time.substring(2, 4);
    } else {
      minutes = time.substring(3, 5);
    }

    if (isNextWindow) {
      this.nextTime = this.addBetweenTime(timeInt, minutes);
    } else {
      timeInt = timeInt + Number(this.productOffering.value.duration);
      this.nextTime = `${timeInt}:${minutes}`;
    }
    return this.nextTime;
  }

  addBetweenTime(timeInt: number, minutes: string) {
    let returnTime: string;
    if (this.hoursBetween === 0.5 && minutes === '30') {
      minutes = '00';
      timeInt += 1;
    } else if (this.hoursBetween === 0.5 && minutes === '00') {
      minutes = '30';
    } else {
      timeInt = timeInt + this.hoursBetween;
    }
    returnTime = `${timeInt}:${minutes}`;
    return returnTime;
  }

  updateTimeWindowsArray(clickEvent: MatCheckboxChange, startTime, endTime) {
    startTime = toTwelveHourTime(startTime);
    endTime = toTwelveHourTime(endTime);

    if (clickEvent.checked) {
      this.timeWindows.push({
        startTime: startTime,
        endTime: endTime,
      });
    } else if (!clickEvent.checked) {
      this.timeWindows = this.timeWindows.filter((window) => {
        return window.startTime !== startTime && window.endTime !== endTime;
      });
    }
    this.timeWindows = this.timeWindows.sort((a, b) => {
      return (
        moment(a.startTime, 'h:mm A').unix() -
        moment(b.startTime, 'h:mm A').unix()
      );
    });

    this.productOffering.next({
      ...this.productOffering.value,
      customAvailabilityTimes: this.timeWindows,
    });
  }

  isNextDay(startTime) {
    let timeInt = parseInt(startTime);
    if (timeInt >= 24) {
      return true;
    } else {
      return false;
    }
  }

  private timeToMilitary(time: string) {
    let militaryTime;
    let timeInt = parseInt(time);
    let minutes;
    if (time.length === 7) {
      minutes = time.substring(2, 4);
    } else {
      minutes = time.substring(3, 5);
    }

    if (time.includes('PM') && timeInt !== 12) {
      timeInt += 12;
    }
    militaryTime = `${timeInt}:${minutes}`;
    return militaryTime;
  }

  private resetCheckboxes() {
    this.customTime1Selected = false;
    this.customTime2Selected = false;
    this.customTime3Selected = false;
    this.customTime4Selected = false;
  }

  toggleAdvancedSettings() {
    this.showAdvanced = !this.showAdvanced;
  }

  updateFlexTimes() {
    this.productOffering.next({
      ...this.productOffering.value,
      flexTimes: this.flexSelected,
    });
  }

  private initFlexTimes() {
    if (this.productOffering.value.flexTimes !== null) {
      this.flexSelected = this.productOffering.value.flexTimes;
    } else {
      this.flexSelected = true;
      this.updateFlexTimes();
    }
  }
}
